import { type ZodType, z } from 'zod';
import { createGameSessionsApi } from './analytics/game-sessions-api';
import { createHeatmapApi } from './analytics/heatmap-api';
import { createAuthApi } from './auth/auth-api';
import { createBoostsApi } from './boosts/boosts-api';
import { createFeaturesApi } from './features/features-api';
import { createGamifyApi } from './gamify/gamify-api';
import { createMontagesApi } from './montages/montages-api';
import { createRewardedAdsApi } from './rewarded-ads/rewarded-ads-api';
import { createTrainersApi } from './trainers/trainers-api';
import type {
    ApiFailure,
    ApiFailureKind,
    EndpointTree,
    UnwrapEndpoints,
    ValidatedApiClient,
    ValidatedFetch,
    ValidatedFetchArgs,
    ValidatedFetchResult,
} from './types/api-client';
import { createUserIdeasApi } from './user-ideas/user-ideas-api';
import { createVideosApi } from './videos/videos-api';

export type GetAuthToken = () => string | null | undefined | Promise<string | null | undefined>;

export type SchemaMismatch = {
    path: string;
    schemaDescription: string;
    error: z.ZodError;
};

export type ApiConfig = {
    baseUrl: string;
    getAuthToken?: GetAuthToken;
    refreshAuthToken?: GetAuthToken;
    superProperties?: object;
    onSchemaMismatch?: (info: SchemaMismatch) => void;
    /**
     * When true, a schema mismatch counts as a failed request (see `createWeModApi` and
     * `createUnwrappedWeModApi` for how failures surface). Recommended for dev/CI so backend/frontend
     * drift is caught immediately. Defaults to false (log-and-pass with the raw response).
     */
    strictValidation?: boolean;
    /** Preserve AbortSignal cancellation for clients that cancel in-flight operations. */
    throwOnAbort?: boolean;
    /** Sees every response before it is read, e.g. for headers the client tracks. */
    onResponse?: (response: Response) => void;
};

export class WeModApiError extends Error {
    public readonly kind: ApiFailureKind;
    public readonly path: string;
    public readonly status?: number;
    public readonly code?: string;
    public readonly hint?: string;
    public readonly issues?: z.ZodError['issues'];

    public constructor(failure: ApiFailure) {
        super(failure.message, { cause: failure.cause });
        this.name = 'WeModApiError';
        this.kind = failure.kind;
        this.path = failure.path;
        this.status = failure.status;
        this.code = failure.code;
        this.hint = failure.hint;
        this.issues = failure.issues;
    }
}

const encodeSuperProperties = (value: object): string => btoa(JSON.stringify(value));

export const createValidatedApiClient = ({
    baseUrl,
    getAuthToken,
    refreshAuthToken,
    superProperties,
    onSchemaMismatch,
    strictValidation = false,
    throwOnAbort = false,
    onResponse,
}: ApiConfig): ValidatedApiClient => {
    const encodedSuperProperties = superProperties !== undefined ? encodeSuperProperties(superProperties) : null;
    let tokenRefresh: Promise<string | null | undefined> | null = null;

    const validatedFetch = async <S extends ZodType>(
        schema: S,
        path: string,
        params: URLSearchParams = new URLSearchParams(),
        options: RequestInit = {},
    ): Promise<ValidatedFetchResult<S>> => {
        const fail = (failure: Omit<ApiFailure, 'path'>): ValidatedFetchResult<S> => ({
            data: null,
            error: failure.message,
            failure: { ...failure, path },
        });

        let url: URL;
        try {
            url = new URL(`${baseUrl}${path}`, baseUrl ? undefined : 'https://relative.invalid');
        } catch {
            return fail({ kind: 'invalid_url', message: `Invalid URL: ${baseUrl}${path}` });
        }

        params.forEach((value, key) => {
            url.searchParams.append(key, value);
        });

        const headers = new Headers(options.headers);
        if (!headers.has('Accept')) {
            headers.set('Accept', 'application/json');
        }
        if (encodedSuperProperties !== null && !headers.has('X-Super-Properties')) {
            headers.set('X-Super-Properties', encodedSuperProperties);
        }
        const managesAuthorization = getAuthToken && !headers.has('Authorization');
        let response: Response;
        try {
            const token = managesAuthorization ? await getAuthToken() : null;
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            if (throwOnAbort) options.signal?.throwIfAborted();
            response = await fetch(baseUrl ? url : `${url.pathname}${url.search}`, { ...options, headers });
            if (response.status === 401 && token && getAuthToken && refreshAuthToken && !options.signal?.aborted) {
                const currentToken = await getAuthToken();
                let refreshedToken = currentToken;
                if (currentToken === token) {
                    tokenRefresh ??= Promise.resolve()
                        .then(refreshAuthToken)
                        .finally(() => {
                            tokenRefresh = null;
                        });
                    refreshedToken = await tokenRefresh;
                }
                if (refreshedToken && !options.signal?.aborted) {
                    const retryHeaders = new Headers(headers);
                    retryHeaders.set('Authorization', `Bearer ${refreshedToken}`);
                    response = await fetch(baseUrl ? url : `${url.pathname}${url.search}`, {
                        ...options,
                        headers: retryHeaders,
                    });
                }
            }
        } catch (cause) {
            if (throwOnAbort) options.signal?.throwIfAborted();
            return fail({ kind: 'network', message: `Network error: ${String(cause)}`, cause });
        }
        onResponse?.(response);

        if (!response.ok) {
            let message = response.statusText || `HTTP ${response.status}`;
            let code: string | undefined;
            let hint: string | undefined;
            try {
                const body: unknown = await response.json();
                if (body !== null && typeof body === 'object') {
                    if ('code' in body && typeof body.code === 'string') {
                        code = body.code;
                        message = body.code;
                    } else if ('error' in body && typeof body.error === 'string') {
                        message = body.error;
                    }
                    if ('message' in body && typeof body.message === 'string') {
                        message = body.message;
                    }
                    if ('hint' in body && typeof body.hint === 'string') {
                        hint = body.hint;
                    }
                }
            } catch {
                // Keep status text when the error body is not JSON.
            }
            if (throwOnAbort) options.signal?.throwIfAborted();
            return fail({ kind: 'http', message, status: response.status, code, hint });
        }

        // 204 has no body to parse, so it's validated as `undefined` (e.g. against `z.void()`).
        let data: unknown;
        if (response.status !== 204) {
            try {
                data = await response.json();
            } catch (cause) {
                if (throwOnAbort) options.signal?.throwIfAborted();
                return fail({ kind: 'parse', message: `JSON parse error: ${String(cause)}`, cause });
            }
        }
        if (throwOnAbort) options.signal?.throwIfAborted();
        const parseResult = await schema.safeParseAsync(data);
        if (throwOnAbort) options.signal?.throwIfAborted();
        if (parseResult.success) {
            return { data: parseResult.data, error: null, failure: null };
        }

        const description = schema.description ?? '(no schema description)';
        const prettyError = z.prettifyError(parseResult.error);
        if (onSchemaMismatch) {
            onSchemaMismatch({ path, schemaDescription: description, error: parseResult.error });
        } else {
            console.warn(`[wemod-api] Schema mismatch: ${description}\n  Path: ${path}\n${prettyError}`);
        }

        // A mismatched 204 has no payload to pass through, so it fails even outside strict mode.
        if (strictValidation || response.status === 204) {
            return fail({
                kind: 'schema',
                message: `Schema mismatch: ${description}\n${prettyError}`,
                issues: parseResult.error.issues,
                cause: parseResult.error,
            });
        }
        return { data: data as z.infer<S>, error: null, failure: null };
    };

    return { validatedFetch };
};

const unwrapClient = ({ validatedFetch }: ValidatedApiClient): ValidatedApiClient => {
    const throwingFetch = async <S extends ZodType>(...args: ValidatedFetchArgs<S>) => {
        const result = await validatedFetch<S>(...args);
        if (result.error !== null) {
            throw new WeModApiError(result.failure);
        }
        return result.data;
    };

    return { validatedFetch: throwingFetch as ValidatedFetch };
};

const buildApi = (client: ValidatedApiClient) => ({
    auth: createAuthApi(client),
    boosts: createBoostsApi(client),
    rewardedAds: createRewardedAdsApi(client),
    analytics: {
        heatmap: createHeatmapApi(client),
        gameSessions: createGameSessionsApi(client),
    },
    features: createFeaturesApi(client),
    gamify: createGamifyApi(client),
    montages: createMontagesApi(client),
    trainers: createTrainersApi(client),
    videos: createVideosApi(client),
    userIdeas: createUserIdeasApi(client),
});

const unwrapApi = <T extends EndpointTree>(api: T) => api as UnwrapEndpoints<T>;

/** Endpoints resolve to `{ data, error, failure }`; failures are `{ data: null, error, failure }` and never reject. */
export const createWeModApi = (config: ApiConfig) => buildApi(createValidatedApiClient(config));

export type WeModApi = ReturnType<typeof createWeModApi>;

/** Endpoints resolve to the parsed payload and reject with `WeModApiError` on failure. */
export const createUnwrappedWeModApi = (config: ApiConfig) =>
    unwrapApi(buildApi(unwrapClient(createValidatedApiClient(config))));

export type UnwrappedWeModApi = ReturnType<typeof createUnwrappedWeModApi>;
